import { ActivatedRoute, NavigationEnd, Router, Scroll } from '@angular/router';
import {
  signal,
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Inject,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
  inject,
  ChangeDetectionStrategy,
  forwardRef,
} from '@angular/core';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs';
import {
  EditorFormEntity,
  FormConfiguration,
  FormMode,
  FormPanel,
  FormProperty,
  FormSaveResult,
  FormTab,
  LayoutConfig,
} from '../../../core/models/form.models';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { resetForm, submitForm } from '../../../core/state/form.actions';
import {
  selectFormEntityError,
  selectFormEntityIsCreated,
  selectFormEntityObject,
  selectFormEntitySaving,
  selectFormEntitySuccess,
} from '../../../core/state';

import { AppState } from 'src/app/core/state/app.state';
import type { BreadcrumbService } from '../../../core/services/breadcrumb.service';
import { BREADCRUMB_SERVICE } from '../../../core/services/breadcrumb.token';
import { FormsService } from '../../../core/services/forms.service';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { LoggerService } from '@services/logger.service';
import { PortalRegistryService } from 'src/app/core/services/portal-registry.service';
import { Store } from '@ngrx/store';
import { TemplatePortal } from '@angular/cdk/portal';
import { ToastService } from '@services/toast.service';
import { TranslateService, TranslatePipe } from '@ngx-translate/core';
import { User } from 'src/app/core/models/core.models';
import { selectUser } from '../../../core/state';
import { take } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass, NgStyle, NgTemplateOutlet, AsyncPipe } from '@angular/common';
import { DynamicFormComponent } from '../dynamic-form/dynamic-form.component';
import { SearchInputComponent } from '../search-input/search-input.component';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';
import { LoadingComponent } from '../loading/loading.component';
import { SuiButtonComponent } from '@sui/button';

@Component({
  selector: 'app-general-form-editor',
  templateUrl: './general-form-editor.component.html',
  styleUrl: './general-form-editor.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgClass,
    NgStyle,
    forwardRef(() => DynamicFormComponent),
    FormsModule,
    ReactiveFormsModule,
    NgTemplateOutlet,
    SearchInputComponent,
    FaIconComponent,
    LoadingComponent,
    SuiButtonComponent,
    AsyncPipe,
    TranslatePipe,
  ],
})
export class GeneralFormEditorComponent implements AfterViewInit {
  @ViewChild('tabsTemplate') tabsTemplate!: TemplateRef<any>;
  @ViewChild('formTemplate') formTemplate!: TemplateRef<any>;

  formGroup: FormGroup;
  formConfig: FormConfiguration;
  formMode: FormMode = FormMode.Create;
  currentUser!: User;

  // * Store active tab index
  activeTabIndex: number = 0;
  searchQuery: string = '';
  panelStyles: { [key: string]: { [key: string]: string } } = {};
  filteredTabs: FormTab[] = [];
  selectedTabKey: string;
  // * Entity name for the form editor (used for state management in ngrx form state)
  editorFormEntityName: EditorFormEntity = EditorFormEntity.Default;

  protected isPopupEditor: boolean = false;

  // * Controls validation visibility - starts false for create mode, enabled on first submit or immediately in edit mode
  protected validationEnabled = signal(false);

  // True while an opt-in Angular Query save (see saveViaMutation) is in flight.
  protected mutationSaving = signal(false);

  // Set once the component is torn down. A mutation-save promise is not cancelled
  // by destruction, so its handlers are guarded on this to avoid firing after the
  // editor (or its host dialog) has closed.
  protected isDestroyed = false;

  protected saving$ = this.store.select(
    selectFormEntitySaving(this.editorFormEntityName),
  );
  protected success$ = this.store.select(
    selectFormEntitySuccess(this.editorFormEntityName),
  );
  protected error$ = this.store.select(
    selectFormEntityError(this.editorFormEntityName),
  );
  protected object$ = this.store.select(
    selectFormEntityObject(this.editorFormEntityName),
  );
  protected isCreated$ = this.store.select(
    selectFormEntityIsCreated(this.editorFormEntityName),
  );
  public searchString$: BehaviorSubject<string> = new BehaviorSubject<string>(
    '',
  );

  protected destroyRef = inject(DestroyRef);
  private portalRegistry: PortalRegistryService = inject(PortalRegistryService);

  protected translateService = inject(TranslateService);
  protected cdr = inject(ChangeDetectorRef);

  constructor(
    protected formBuilder: FormBuilder,
    protected formService: FormsService,
    protected toastService: ToastService,
    protected store: Store<AppState>,
    protected logger: LoggerService,
    protected router: Router,
    protected activatedRoute: ActivatedRoute,
    @Inject(BREADCRUMB_SERVICE) protected breadcrumbService: BreadcrumbService,
    protected viewContainerRef: ViewContainerRef,
  ) {}

  ngOnInit(): void {
    this.destroyRef.onDestroy(() => (this.isDestroyed = true));

    this.store.dispatch(resetForm({ entity: this.editorFormEntityName }));

    this.store
      .select(selectUser)
      .pipe(
        filter((u): u is User => !!u), // ensure non-null
        take(1), // take a single value and complete
      )
      .subscribe((u) => (this.currentUser = u));

    // * Trigger toast notifications based on form submission results
    this.success$
      .pipe(
        filter(Boolean),
        withLatestFrom(this.object$, this.isCreated$),
        tap(([_, object, isCreated]) => {
          this.showSuccessToast(object, isCreated);
          if (isCreated) {
            this.onObjectCreated(object);
          } else {
            this.onObjectUpdated(object);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.error$
      .pipe(
        filter(Boolean),
        tap((error) => this.showErrorToast(error)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    this.router.events
      .pipe(
        // ! Do we need instance of Scroll - ??? - find out!
        filter(
          (event) => event instanceof NavigationEnd || event instanceof Scroll,
        ),
        switchMap(() => {
          let route = this.activatedRoute;
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route.params;
        }),
        map((params) => params['tab']),
        distinctUntilChanged(),
        tap((tabParam) => {
          if (tabParam) {
            this.logger.debug('Tab param:', tabParam);
            this.selectedTabKey = tabParam;
            const tab = this.formConfig.tabs.find(
              (tab) => tab.key === tabParam,
            );
            this.setActiveTab(tab, false);
          }
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();

    // * Load form configuration
    this.formConfig = this.filterFormConfigurationByFormModeAndUserPermissions(
      this.loadFormConfig(),
      this.formMode,
      this.currentUser,
    );

    this.formConfig.tabs.forEach((tab, index) => {
      tab.tabIndex = index;
    });

    this.selectedTabKey = this.formConfig.tabs[0]?.key;

    // * Initialize the form group based on config
    this.formGroup = this.formService.createFormGroupByFormConfig(
      this.formConfig,
    );

    this.buildFormControllAndPanelStyles(this.formConfig);
    this.filterTabs();
  }

  /**
   * Determines if components should be eagerly loaded (all tabs rendered at once)
   * Override in child components to customize behavior
   *
   * @returns true if all tabs should be rendered immediately (enables search across all tabs)
   */
  protected shouldEagerLoadComponents(): boolean {
    // * Enable for Role and User editors to support cross-tab search
    return (
      this.editorFormEntityName === EditorFormEntity.Role ||
      this.editorFormEntityName === EditorFormEntity.User ||
      this.editorFormEntityName === EditorFormEntity.UserGroup
    );
  }

  ngAfterViewInit(): void {
    const tabsPortal = new TemplatePortal(
      this.tabsTemplate,
      this.viewContainerRef,
    );
    const formPortal = new TemplatePortal(
      this.formTemplate,
      this.viewContainerRef,
    );

    // ! register portal Tabs and Tabs Content sections for the Popup
    if (this.isPopupEditor) {
      this.portalRegistry.set('general-form-editor-tabs', tabsPortal);
      this.portalRegistry.set('general-form-editor-form', formPortal);
    }
  }

  protected setupValidationSync(): void {
    this.formGroup.statusChanges
      .pipe(
        debounceTime(150),
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.highlightInvalidTabs();
          this.cdr.detectChanges();
        }),
      )
      .subscribe();
  }

  private showSuccessToast(object: any, isCreated: boolean): void {
    const detail = isCreated
      ? this.translateService.instant(
          'editor-form.toast.object-created-successfully',
          {
            object: this.editorFormEntityName,
          },
        )
      : this.translateService.instant(
          'editor-form.toast.object-updated-successfully',
          {
            object: this.editorFormEntityName,
          },
        );

    this.toastService.showSuccess(detail);

    isCreated ? this.onObjectCreated(object) : this.onObjectUpdated(object);
  }

  private showErrorToast(error: string): void {
    this.toastService.showError(error);
  }

  onObjectCreated(object) {
    this.logger.debug('New object created:', object);
  }

  onObjectUpdated(object) {
    this.logger.debug('Object updated:', object);
  }

  // Dynamically create form group based on configuration
  buildFormControllAndPanelStyles(config: FormConfiguration): FormGroup {
    const formGroup = this.formBuilder.group({});
    config.tabs.forEach((tab) => {
      this.logger.debug('Building tab buildForm:', tab.label);
      tab.panels.forEach((panel) => {
        this.buildPanelControls(panel, formGroup);
        this.buildPanelsStyles(panel);
      });
    });

    return formGroup;
  }

  filterFormConfigurationByFormModeAndUserPermissions(
    formConfig: FormConfiguration,
    formMode: FormMode,
    user: User,
  ): FormConfiguration {
    const filteredTabs = formConfig.tabs
      .filter((tab) => {
        // Check if the tab is available in the current form mode
        return (
          (!tab.availableInFormModes ||
            tab.availableInFormModes.includes(formMode)) &&
          (!tab.userAccessLevel ||
            tab.userAccessLevel <= user.access_level_id) &&
          (!tab.userPermission || user.feature_components[tab.userPermission])
        );
      })
      .map((tab) => ({
        ...tab,
        // Filter the panels within the tab
        panels: this.filterPanelsByFormModeAndUserPermissions(
          tab.panels,
          formMode,
          user,
        ),
      }));

    return {
      ...formConfig,
      tabs: filteredTabs,
    };
  }

  filterPanelsByFormModeAndUserPermissions(
    panels: FormPanel[],
    formMode: FormMode,
    user: User,
  ): FormPanel[] {
    return panels
      .filter(
        (panel) =>
          (!panel.availableInFormModes ||
            panel.availableInFormModes.includes(formMode)) &&
          (!panel.userAccessLevel ||
            panel.userAccessLevel <= user.access_level_id) &&
          (!panel.userPermission ||
            user.feature_components[panel.userPermission]),
      )
      .map((panel) => {
        const filteredChildPanels = panel.panels
          ? this.filterPanelsByFormModeAndUserPermissions(
              panel.panels,
              formMode,
              user,
            )
          : [];

        const filteredItems = panel.items
          ? this.filterItemsByFormModeAndUserPermissions(
              panel.items,
              formMode,
              user,
            )
          : [];

        return {
          ...panel,
          panels: filteredChildPanels,
          items: filteredItems,
        };
      })
      .filter(
        (panel) =>
          (panel.items?.length ?? 0) > 0 || (panel.panels?.length ?? 0) > 0,
      );
  }
  filterItemsByFormModeAndUserPermissions(
    items: FormProperty[],
    formMode: FormMode,
    user: User,
  ): FormProperty[] {
    return items.filter((item) => {
      return (
        (!item.availableInFormModes ||
          item.availableInFormModes.includes(formMode)) &&
        (!item.userAccessLevel ||
          item.userAccessLevel <= user.access_level_id) &&
        (!item.userPermission || user.feature_components[item.userPermission])
      );
    });
  }

  // Build controls for a panel recursively, including child panels
  buildPanelControls(panel: FormPanel, formGroup: FormGroup): void {
    panel.items?.forEach((item) => {
      if (formGroup.contains(item.propertyName) === false) {
        formGroup.addControl(item.propertyName, this.createControl(item));
      }
    });

    // Handle child panels recursively
    panel.panels?.forEach((childPanel) => {
      this.buildPanelControls(childPanel, formGroup);
    });
  }

  buildPanelsStyles(panel: FormPanel) {
    this.panelStyles[panel.panelName] = this.getPanelStyles(panel.layoutConfig);

    // Handle child panels recursively
    panel.panels?.forEach((childPanel) => {
      // Precompute and store styles for each panel
      this.panelStyles[panel.panelName] = this.getPanelStyles(
        panel.layoutConfig,
      );
      this.buildPanelsStyles(childPanel);
    });
  }

  // Helper method to create a form control for each form item
  createControl(item: FormProperty) {
    const control = this.formBuilder.control(
      {
        value: item.defaultValue || '',
        disabled: item.controlType === 'hidden',
      },
      item.validators || null,
    );

    return control;
  }

  // Method to switch between tabs
  setActiveTab(tab: FormTab, updateUrl: boolean = true) {
    this.selectedTabKey = tab.key;
    this.activeTabIndex = tab.tabIndex;
    if (!this.isPopupEditor) {
      this.breadcrumbService.currentTab = tab.label;
      if (updateUrl) {
        this.router.navigate([tab.key], { relativeTo: this.activatedRoute });
      }
    }
  }

  // Example method to load form configuration (replace with actual data source)
  loadFormConfig(params?: any): FormConfiguration {
    return null;
  }

  getPanelStyles(layoutConfig: LayoutConfig): { [key: string]: string } {
    const styleMappings: { [key: string]: string } = {
      direction: 'flex-direction',
      justifyContent: 'justify-content',
      alignItems: 'align-items',
      flexWrap: 'flex-wrap',
      gap: 'gap',
      width: 'width',
      height: 'height',
      padding: 'padding',
      border: 'border',
    };

    const styles: { [key: string]: string } = {
      display: layoutConfig.direction ? 'flex' : undefined, // Set display to 'flex' only if 'direction' is set
    };

    Object.keys(styleMappings).forEach((key) => {
      const cssProperty = styleMappings[key];
      if (layoutConfig[key]) {
        styles[cssProperty] = layoutConfig[key];
      }
    });

    return styles;
  }
  /** override this method in child components for customizing the form data success saving */
  protected onSubmitSuccess(formData: any) {}

  /**
   * Opt-in Angular Query save path. An editor overrides this to run its mutation and
   * resolve with the saved object; returning a promise makes the base own the
   * success/error handling (toast + onObjectCreated/Updated) and the saving spinner,
   * bypassing the NgRx submitForm mechanism. Returning null keeps the NgRx path.
   */
  protected saveViaMutation(formData: any): Promise<FormSaveResult> | null {
    return null;
  }

  private handleMutationSuccess(object: any, isCreated: boolean): void {
    const detail = isCreated
      ? this.translateService.instant(
          'editor-form.toast.object-created-successfully',
          { object: this.editorFormEntityName },
        )
      : this.translateService.instant(
          'editor-form.toast.object-updated-successfully',
          { object: this.editorFormEntityName },
        );

    // Toast is app-level feedback - fire it even if the editor's host dialog has
    // already closed, since the mutation promise resolves independently of teardown.
    this.toastService.showSuccess(detail);

    // Navigation is editor-scoped — skip once the editor (or its dialog) is gone,
    // to avoid a stray navigation after an optimistic dialog close.
    if (this.isDestroyed) {
      return;
    }

    if (isCreated) {
      this.onObjectCreated(object);
    } else {
      this.onObjectUpdated(object);
    }
  }

  private handleMutationError(error: any): void {
    this.showErrorToast(
      error?.error?.message ?? error?.message ?? String(error),
    );
  }

  // Form submission handler
  /**
   * Mutation path: returns a promise that settles with the save (rejects on failure),
   * so a dialog can close optimistically and still tell its opener when the row is
   * really persisted. NgRx path: returns nothing, as before.
   */
  submit(): Promise<unknown> | void {
    // 1) Validate + (if invalid) highlight tabs + toast + logs
    if (!this.validateAndHandleInvalidForm()) {
      return;
    }

    // 2) Valid form path: reset tab errors, filter tabs
    this.resetTabErrorsAndFilter();

    this.logger.debug(
      'Form is valid. Proceeding with submission.',
      this.editorFormEntityName,
    );

    // 3a) Opt-in Angular Query path (when a subclass overrides saveViaMutation)
    const mutationSave = this.saveViaMutation(this.formGroup.getRawValue());
    if (mutationSave) {
      this.mutationSaving.set(true);
      // Handlers run even after teardown: the toast must survive an optimistic
      // dialog close. Editor-scoped work is guarded on isDestroyed inside them.
      const settled = mutationSave
        .then((result) =>
          this.handleMutationSuccess(result.object, result.isCreated),
        )
        .catch((error) => {
          this.handleMutationError(error);
          throw error;
        })
        .finally(() => this.mutationSaving.set(false));
      // * the failure is already reported above; a caller that drops the handle
      // * must not turn it into an unhandled rejection
      settled.catch(() => undefined);
      return settled;
    }

    // 3b) NgRx form-state path
    this.store.dispatch(
      submitForm({
        entity: this.editorFormEntityName,
        formData: this.formGroup.value,
      }),
    );

    this.onSubmitSuccess(this.formGroup.getRawValue());
  }

  /**
   * Runs validation and handles invalid UX/logging.
   * @returns true if valid, false if invalid (and handled).
   */
  public validateAndHandleInvalidForm(): boolean {
    // * Enable validation visibility from this point forward
    this.validationEnabled.set(true);

    this.formService.validateFormConfig(
      this.formConfig,
      this.formGroup,
      this.formMode,
    );

    if (this.formGroup.valid) {
      return true;
    }

    this.logger.debug('Invalid form. Please correct the errors and try again.');
    this.logger.debug(this.formGroup.getRawValue());

    this.logInvalidControls(this.formGroup);

    // Highlight the tabs with validation errors
    this.highlightInvalidTabs();

    this.toastService.showError(
      'Invalid form. Please correct the errors and try again.',
    );

    return false;
  }

  private logInvalidControls(formGroup: FormGroup): void {
    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (!control || control.valid) {
        return;
      }

      const errors = control.errors ?? {};
      const errorMessages = Object.keys(errors).map(
        (error) => `${error}: ${errors[error]}`,
      );

      this.logger.debug(`Invalid Field - ${key}: ${errorMessages.join(', ')}`);
    });
  }

  private resetTabErrorsAndFilter(): void {
    this.formConfig.tabs.forEach((tab) => {
      tab.countErrors = 0;
    });

    this.filterTabs();
  }

  // Highlight the tabs that contain fields with errors
  highlightInvalidTabs() {
    // * Only highlight if validation is enabled
    if (!this.validationEnabled()) {
      return;
    }

    this.formConfig.tabs.forEach((tab, tabIndex) => {
      // Initialize error count for the tab
      let errorCount = 0;

      tab.panels?.forEach((panel) => {
        // Calculate the number of errors for the panel
        errorCount += this.countPanelErrors(panel);
      });

      tab.countErrors = errorCount; // Store the number of errors for this tab
    });

    this.filterTabs(); // Update filtered tabs based on search query and errors
  }

  countPanelErrors(panel: FormPanel): number {
    let errorCount = 0;

    // Count errors in the current panel's controls
    errorCount +=
      panel.items?.filter(
        (item) => this.formGroup.get([item.propertyName])?.invalid,
      ).length || 0;

    // Recursively count errors in the child panels
    panel.panels?.forEach((childPanel) => {
      errorCount += this.countPanelErrors(childPanel);
    });

    return errorCount;
  }

  onCancel() {
    this.logger.debug('Cancel');
  }

  doesPanelContainMatchingControl(panel: FormPanel, query: string): boolean {
    const hasMatchingControl = panel.items?.some((item: FormProperty) => {
      if (item.controlType === 'component') {
        if (
          item.compnentInstance &&
          typeof item.compnentInstance.getSearchStringMatchesCount ===
            'function'
        ) {
          const matchCount =
            item.compnentInstance.getSearchStringMatchesCount();
          return matchCount > 0;
        }

        return false;
      }

      return item.propertyLabel?.toLowerCase().includes(query);
    });

    const hasMatchingChildPanel = panel.panels?.some((childPanel: FormPanel) =>
      this.doesPanelContainMatchingControl(childPanel, query),
    );

    // Return true if either the current panel or any child panels have a match
    return hasMatchingControl || hasMatchingChildPanel;
  }

  // Filters tabs based on the search query - searches across ALL tabs
  filterTabs(): void {
    const searchQuery = this.searchQuery.toLowerCase();
    this.searchString$.next(searchQuery);
    this.filteredTabs = [];

    if (searchQuery.length === 0) {
      this.filteredTabs = [...this.formConfig.tabs].filter(
        (tab) =>
          !tab.availableInFormModes ||
          tab.availableInFormModes.includes(this.formMode),
      );
      return;
    }

    // * Search across ALL tabs, not just the active one
    const tabsWithMatches: FormTab[] = [];

    this.formConfig.tabs.forEach((tab) => {
      const filteredPanels = tab.panels.filter((panel) =>
        this.doesPanelContainMatchingControl(panel, searchQuery),
      );

      if (filteredPanels.length > 0) {
        tabsWithMatches.push({
          ...tab,
          panels: filteredPanels,
        });
      }
    });

    if (tabsWithMatches.length > 0) {
      this.filteredTabs = tabsWithMatches;

      // * Switch to first tab with match if current tab has no matches
      const currentTabHasMatch = tabsWithMatches.some(
        (tab) => tab.key === this.selectedTabKey,
      );

      if (!currentTabHasMatch) {
        // * Current tab has no matches, switch to first tab with matches
        this.setActiveTab(tabsWithMatches[0], true);
      }
      // * else: Stay on current tab if it has matches
    } else {
      // * No matches found in any tab - show empty results
      this.filteredTabs = [];
    }

    setTimeout(() => {
      this.cdr.detectChanges();
    });
  }

  addTab(newTab: Partial<FormTab> = {}, index?: number): void {
    const defaultTab: FormTab = {
      label: 'New Tab',
      key: `tab-${this.formConfig.tabs.length + 1}`,
      icon: ['fas', 'plus'],
      panels: [],
      tabIndex: this.formConfig.tabs.length,
      name: '',
      countErrors: 0,
    };

    const tabToAdd: FormTab = { ...defaultTab, ...newTab };

    if (
      index !== undefined &&
      index >= 0 &&
      index <= this.formConfig.tabs.length
    ) {
      this.formConfig.tabs.splice(index, 0, tabToAdd);
    } else {
      this.formConfig.tabs.push(tabToAdd);
    }

    // Update tab indexes after adding the new tab
    this.formConfig.tabs.forEach((tab, idx) => (tab.tabIndex = idx));
    this.createFormTabControls(tabToAdd, this.formGroup);
    this.filterTabs(); // Update the filtered tabs to reflect the new tab
  }

  setTabIcon(tabIdentifier: number | string, newIcon: IconProp): void {
    const tab =
      typeof tabIdentifier === 'number'
        ? this.formConfig.tabs[tabIdentifier]
        : this.formConfig.tabs.find((tab) => tab.key === tabIdentifier);

    if (tab) {
      tab.icon = newIcon;
    }
  }

  setTabIndex(tabIdentifier: number | string, newIndex: number): void {
    const tabIndex =
      typeof tabIdentifier === 'number'
        ? tabIdentifier
        : this.formConfig.tabs.findIndex((tab) => tab.key === tabIdentifier);

    if (
      tabIndex > -1 &&
      newIndex >= 0 &&
      newIndex < this.formConfig.tabs.length
    ) {
      const [tab] = this.formConfig.tabs.splice(tabIndex, 1);
      this.formConfig.tabs.splice(newIndex, 0, tab);

      // Update tab indexes after repositioning
      this.formConfig.tabs.forEach((tab, index) => (tab.tabIndex = index));
      this.filterTabs(); // Update the filtered tabs to reflect the new order
    }
  }

  renameTab(tabIdentifier: number | string, newLabel: string): void {
    const tab =
      typeof tabIdentifier === 'number'
        ? this.formConfig.tabs[tabIdentifier]
        : this.formConfig.tabs.find((tab) => tab.key === tabIdentifier);

    if (tab) {
      tab.label = newLabel;
    }
  }

  removeTab(tabIdentifier: number | string): void {
    const tabIndex =
      typeof tabIdentifier === 'number'
        ? tabIdentifier
        : this.formConfig.tabs.findIndex((tab) => tab.key === tabIdentifier);

    if (tabIndex > -1) {
      this.formConfig.tabs.splice(tabIndex, 1);
      // Reassign tab indexes after removing a tab
      this.formConfig.tabs.forEach((tab, index) => (tab.tabIndex = index));
      this.filterTabs(); // Update the filtered tabs to reflect the removed tab
    }
  }

  updateTab(
    tabIdentifier: number | string,
    updatedProperties: Partial<FormTab>,
  ): void {
    const tab =
      typeof tabIdentifier === 'number'
        ? this.formConfig.tabs[tabIdentifier]
        : this.formConfig.tabs.find((tab) => tab.key === tabIdentifier);

    if (tab) {
      Object.assign(tab, updatedProperties);
      this.filterTabs(); // Update the filtered tabs to reflect any changes if needed
    } else {
      this.logger.warn(`Tab with identifier '${tabIdentifier}' not found.`);
    }
  }

  replaceTab(tabIdentifier: number | string, newTab: FormTab): void {
    const tabIndex =
      typeof tabIdentifier === 'number'
        ? tabIdentifier
        : this.formConfig.tabs.findIndex((tab) => tab.key === tabIdentifier);

    if (tabIndex > -1) {
      // Insert the new tab at the specified index
      this.formConfig.tabs[tabIndex] = {
        ...newTab,
        tabIndex: tabIndex, // Ensure the correct index is set for the new tab
      };

      // Update all tab indexes to maintain consistency
      this.formConfig.tabs.forEach((tab, index) => (tab.tabIndex = index));
      this.createFormTabControls(newTab, this.formGroup);

      this.filterTabs(); // Refresh filtered tabs if needed
    } else {
      console.warn(`Tab with identifier '${tabIdentifier}' not found.`);
    }
  }

  createFormTabControls(formTab: FormTab, formGroup: FormGroup): void {
    formTab.panels.forEach((panel) => {
      this.createPanelControlsAndStyles(panel, formGroup);
    });
  }

  protected createPanelControlsAndStyles(
    panel: FormPanel,
    formGroup: FormGroup,
  ): void {
    // Create controls for each property in the current panel
    panel.items?.forEach((item) => {
      if (formGroup.contains(item.propertyName) === false) {
        formGroup.addControl(item.propertyName, this.createControl(item));
      }
    });

    this.buildPanelsStyles(panel);

    // Recursively create controls for child panels
    panel.panels?.forEach((childPanel) => {
      this.createPanelControlsAndStyles(childPanel, formGroup);
    });
  }
}
